// The boat notes an eval turn proposed (TOM-616), as the case drawer shows
// them under each repeat: who proposed each, what it said and cited, and
// whether the notebook kept it, refused it (the server's plain reason for the
// store's code) or marked a note contradicted. The words of each part are the
// boat notebook page's (../notebook.ts). Pure: tested in proposedNotes.test.ts.

import { NOTEBOOK_AREAS, kindText, type NotebookKind } from '../notebook';
import { agentLabel } from './agents';
import type { EvalProposedNote } from './types';

/** How the note was proposed, in words: Neo, an expert Neo asked, or an expert on its own. */
export function noteWho(n: Pick<EvalProposedNote, 'by' | 'via'>): string {
  if (n.by === 'neo') return 'Neo (chat)';
  const name = agentLabel(n.by);
  return n.via === 'consult' ? `${name}, asked by Neo` : name;
}

const ACTION_TEXT: Readonly<Record<string, string>> = {
  add: 'New note',
  reaffirm: 'Seen again',
  contradict: 'Says a note is wrong',
  confirm: 'Confirms a note',
  forget: 'Forgets a note',
};

/** What the note asked for: a new note, or a change to one. */
export function noteActionText(action: string | null): string {
  if (action === null) return 'A note';
  return ACTION_TEXT[action] ?? action;
}

/** The part of the boat, as the notebook page names it. */
export function noteAreaText(area: string | null): string | null {
  if (area === null) return null;
  return NOTEBOOK_AREAS.find((a) => a.area === area)?.title ?? area;
}

/** The kind of note, as the notebook page names it. */
export function noteKindText(kind: string | null): string | null {
  return kind === null ? null : kindText(kind as NotebookKind);
}

const KEPT_TEXT: Readonly<Record<string, string>> = {
  added: 'Kept',
  replaced: 'Kept, over a note no longer in use',
  reaffirmed: 'Kept: seen again',
  confirmed: 'Kept: confirmed',
  corrected: "Kept: now the owner's words",
  forgotten: 'Kept: forgotten',
  unchanged: 'Kept: nothing changed',
  contradicted: 'Contradicted: the note is no longer used',
  disputed: "Contradicted: an owner's note, kept and flagged to ask the owner",
};

/** The outcome's words and its badge's tone. */
export function noteOutcomeText(n: Pick<EvalProposedNote, 'outcome' | 'change'>): {
  label: string;
  tone: 'succeeded' | 'warn' | 'bad';
} {
  if (n.outcome === 'refused') return { label: 'Refused', tone: 'bad' };
  const label =
    (n.change !== null ? KEPT_TEXT[n.change] : undefined) ??
    (n.outcome === 'contradicted' ? 'Contradicted' : 'Kept');
  return { label, tone: n.outcome === 'contradicted' ? 'warn' : 'succeeded' };
}

/**
 * Plain reasons for refusal codes newer than the server's own list, which it
 * sends back as the bare code (cloud proposed-notes.ts EVAL_NOTE_REFUSAL_REASON).
 * Each later PR that adds a store or collector code adds its line here.
 */
const LATER_REASONS: Readonly<Record<string, string>> = {
  // The consult's collector (TOM-613).
  profile_echo: 'It repeated something Neo learned about the owner from earlier conversations.',
};

/** Why a refused note was refused, in plain words: the server's reason, or this page's for a newer code. */
export function noteReasonText(n: Pick<EvalProposedNote, 'reason' | 'refusal'>): string {
  const known = n.reason !== null && n.reason !== n.refusal;
  if (known) return n.reason!;
  return (
    (n.refusal !== null ? LATER_REASONS[n.refusal] : undefined) ??
    n.reason ??
    n.refusal ??
    'not recorded'
  );
}

/** The entry standing for the notes past the most a result keeps (cloud proposed-notes.ts). */
export const NOTES_PAST_LIMIT = 'past_record_limit';

/** The stand-in for notes left out past the limit, not a note of its own. */
export function isPastLimit(n: Pick<EvalProposedNote, 'refusal'>): boolean {
  return n.refusal === NOTES_PAST_LIMIT;
}

/** "2 kept, 1 refused": the repeat's notes at a glance; the stand-in for left-out notes is not one. */
export function notesCountText(notes: readonly EvalProposedNote[]): string {
  const count = (o: EvalProposedNote['outcome']) =>
    notes.filter((n) => n.outcome === o && !isPastLimit(n)).length;
  const parts = [
    [count('kept'), 'kept'],
    [count('contradicted'), 'contradicted'],
    [count('refused'), 'refused'],
  ] as const;
  return parts
    .filter(([n]) => n > 0)
    .map(([n, word]) => `${n} ${word}`)
    .join(', ');
}

/** The case grades the notes its turn writes: Neo's (expectNotes) or an expert's (expectExpertNotes). */
export function gradesNotes(definition: Record<string, unknown> | null | undefined): boolean {
  return (
    definition !== null &&
    definition !== undefined &&
    (definition.expectNotes != null || definition.expectExpertNotes != null)
  );
}

/**
 * Whether the drawer shows a repeat's notes section: when the turn proposed
 * any, or when the case grades notes and the turn proposed none. A run stored
 * before notes were recorded (null) shows nothing.
 */
export function showNotesSection(
  notes: readonly EvalProposedNote[] | null | undefined,
  definition: Record<string, unknown> | null | undefined
): boolean {
  if (notes === null || notes === undefined) return false;
  return notes.length > 0 || gradesNotes(definition);
}
